import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks/useTran";
import { useUnsavedChanges, useFormDirty } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";
import { useSurveyLibraryStore } from "../hooks/useSurveyLibraryStore";
import SurveysFormHeader from "../components/SurveysFormHeader";
import SurveyFormCard from "../components/SurveyFormCard";
import QuestionLibraryDialog from "../components/QuestionLibraryDialog";
import SurveyQuestionLibraryCard from "../components/SurveyQuestionLibraryCard";
import { PATHS } from "@/lib/router";
import { SURVEY_STATUS } from "../utils/survey-manager.helper";
import { flushSync } from "react-dom";

const EMPTY_FORM = {
  title: "",
  description: "",
  expiresAt: "",
  type: "prospect",
  status: "draft",
  questions: [],
};

const CreateSurveyPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const store = useSurveyLibraryStore();
  const { surveyId } = useParams();
  const isEdit = Boolean(surveyId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [originalStatus, setOriginalStatus] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  const isDirty = useFormDirty(
    { form, selectedQuestions },
    loadingData ? "loading" : surveyId || "new",
  );
  const { showDialog, confirmDiscard, cancelDiscard, bypass } =
    useUnsavedChanges(isDirty && !loadingData);

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const survey = await store.fetchSurveyById(surveyId);
        setForm({
          title: survey.name || "",
          description: survey.description || "",
          type: survey.type || "prospect",
          status: survey.status || "draft",
          expiresAt: survey.expiresAt
            ? new Date(survey.expiresAt).toISOString().split("T")[0]
            : "",
        });
        setOriginalStatus(survey.status);
        setSelectedQuestions(survey.questions || []);
      } catch {
        navigate(PATHS.SURVEY_LIBRARY);
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [surveyId]);
  //const isLocked = isEdit && originalStatus === SURVEY_STATUS.active;

  // ── Field change ──────────────────────────────────────────────────────
  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ── Validate ──────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = t("Survey title is required");
    if (selectedQuestions.length === 0) {
      e.questions = t("At least one question is required");
    }
    if (form.status === "active" && !form.expiresAt) {
      e.expiresAt = t("Expiry date is required when survey is active");
    }
    return e;
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    //if (isLocked) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const payload = {
      name: form.title.trim(),
      description: form.description.trim(),
      type: form.type,
      status: form.status,
      expiresAt: form.expiresAt || undefined,
      questions: selectedQuestions.map((q) => q._id ?? q),
    };

    try {
      if (isEdit) {
        await store.updateSurvey(surveyId, payload); // ✅ surveyId not id
      } else {
        await store.createSurvey(payload);
      }
      bypass();
      navigate(PATHS.SURVEY_LIBRARY);
    } catch {
      /* toast shown in store */
    }
  };

  // ── Dialog confirm ────────────────────────────────────────────────────
  const handleConfirmFromDialog = (picked) => {
    setSelectedQuestions((prev) => {
      const existingIds = new Set(prev.map((q) => q._id ?? q));
      return [...prev, ...picked.filter((q) => !existingIds.has(q._id))];
    });
    setShowQuestionDialog(false);
    if (errors.questions) setErrors((prev) => ({ ...prev, questions: "" }));
  };

  const handleRemoveQuestion = (questionId) => {
    setSelectedQuestions((prev) =>
      prev.filter((q) => (q._id ?? q) !== questionId),
    );
  };
  const handleReorderQuestions = (reordered) => {
    setSelectedQuestions(reordered);
  };

  // ── Loading state (edit only) ─────────────────────────────────────────
  if (loadingData) {
    return (
      <div className="h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading survey...
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="min-h-screen flex flex-col">
      <SurveysFormHeader
        submitting={store.submitting}
        label={isEdit ? t("Save changes") : t("Save survey")}
        //disabled={isLocked}
      />

      {/* {isLocked && (
                <div className="mx-3 mt-3 flex items-center gap-2 rounded-lg border
                    border-yellow-200 bg-yellow-50 px-4 py-2.5 text-sm text-yellow-700
                    dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-400">
                    <span>⚠️</span>
                    <span>
                        {t("This survey is active and cannot be edited. Close it first to make changes.")}
                    </span>
                </div>
            )} */}

      <div className="flex-1 p-3 grid gap-3 lg:grid-cols-3">
        <SurveyFormCard
          form={form}
          errors={errors}
          onFieldChange={handleFieldChange}
        />

        <SurveyQuestionLibraryCard
          selectedQuestions={selectedQuestions}
          onOpenLibrary={() => setShowQuestionDialog(true)}
          onRemoveQuestion={handleRemoveQuestion}
          onReorderQuestions={handleReorderQuestions}
          error={errors.questions}
          className="lg:col-span-2"
        />
      </div>

      <QuestionLibraryDialog
        isOpen={showQuestionDialog}
        onClose={() => setShowQuestionDialog(false)}
        onConfirm={handleConfirmFromDialog}
        alreadySelected={selectedQuestions.map((q) => q._id ?? q)}
      />
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </form>
  );
});

export default CreateSurveyPage;
